import type { FmElementScale } from './elements/elements.types';
import type { FmSection, FmSectionDensity, FmSectionHeadline } from './sections/section.types';

/**
 * Chapter band tones: `ink` is the deep statement, `light` and `mist` are for
 * reading, `paper` holds working evidence (tables, ledgers) one step off light,
 * `forest` is the dark map band.
 */
export const FM_REPORT_CHAPTER_TONES = ['light', 'mist', 'forest', 'ink', 'paper'] as const;
export type FmReportChapterTone = (typeof FM_REPORT_CHAPTER_TONES)[number];
export type FmReportChapterSectionRole = 'lead' | 'evidence' | 'detail' | 'action';
export type FmReportChapterConversionRole = 'orient' | 'build-trust' | 'compare' | 'decide' | 'act' | 'retain';

export interface FmReportChapterStateBehavior {
  loading: string;
  empty: string;
  partial: string;
  stale: string;
  locked: string;
  error: string;
}

export interface FmReportChapterAssistantDescriptor {
  /** Owner-readable action copy supplied by the descriptor locale. */
  actionLabel: string;
  /** Assistant scope is exact and server-derived; it is never inferred from visible copy. */
  scope: 'property' | 'campaign';
  propertyId: string;
  campaignId?: string;
  sourceId: string;
  revision: string;
  changeIds: readonly [string, ...string[]];
  citationIds: readonly [string, ...string[]];
  /** Report Assistants can explain or navigate; mutations remain explicit page actions. */
  allowedTools: readonly ['explain', 'navigate'];
  confirmationRequiredFor: readonly [string, ...string[]];
  /** Put context help before selectors when controls belong to the lead section. */
  placement?: 'before-sections';
}

export interface FmReportChapterAssistantTools {
  explain: (context: FmReportChapterAssistantDescriptor) => void;
  navigate: (context: FmReportChapterAssistantDescriptor) => void;
}

export interface FmReportChapterConfirmationDescriptor {
  actionId: string;
  transitions: readonly [{
    entityId: string;
    prior: {
      sourceId: string;
      campaignId: string | null;
    };
    next: {
      sourceId: string;
      campaignId: string | null;
    };
  }, ...{
    entityId: string;
    prior: {
      sourceId: string;
      campaignId: string | null;
    };
    next: {
      sourceId: string;
      campaignId: string | null;
    };
  }[]];
  proposalRevision: string;
  humanConfirmationRequired: true;
}

/** Reviewable data/action/state ownership for one report chapter. */
export interface FmReportChapterRecipeDescriptor {
  requiredServedFields: readonly [string, ...string[]];
  requiredProvenance: readonly [string, ...string[]];
  primaryAction: string;
  primaryActionSectionId?: string;
  expertReveal: string;
  expertSectionIds?: readonly [string, ...string[]];
  stateBehavior: FmReportChapterStateBehavior;
  assistant?: FmReportChapterAssistantDescriptor;
  confirmation?: FmReportChapterConfirmationDescriptor;
}

export interface FmReportChapterSectionDescriptor {
  /** Reading role controls DS-owned rhythm; it is not a caller-owned layout slot. */
  role: FmReportChapterSectionRole;
  /** One section: an arrangement placing typed elements. */
  section: FmSection;
}

export interface FmReportChapterDetailView {
  /** Stable semantic identity for the controlled expert view. */
  id: string;
  /** Owner-readable selector label. */
  label: string;
  /** Detail-role section ids rendered when this view is active. */
  sectionIds: string[];
}

/**
 * One owner-facing report chapter composed from sections. The chapter owns the
 * semantic region, navigation target, H2, background, and section rhythm; its
 * copy (eyebrow, title, answer, transition) stays chapter-owned. Routes pass
 * data and domain callbacks only.
 */
export interface FmReportChapterDescriptor {
  id: string;
  /**
   * Rail label, ONLY when the rail needs shorter words than the heading. The
   * chapter title is the single source for both, so an omitted navLabel means
   * "use the title" and a navLabel EQUAL to the title is rejected as an echo
   * Read it through `chapterNavLabel`, never directly.
   */
  navLabel?: string;
  /**
   * Words for the narrow (mobile) rail when even the rail label clips there.
   * Omitted, the narrow rail shows the rail label.
   */
  navShortLabel?: string;
  title: string;
  /** The owner question this chapter answers, in owner language. */
  ownerQuestion: string;
  /** Literal answer visible before supporting detail. */
  literalAnswer: string;
  /** Section id that owns the chapter's primary visual beat. */
  dominantBeat: string;
  /** Reading transition into the next chapter. */
  transition: string;
  /** Complete section-id order used when the chapter collapses to one column. */
  mobileOrder: string[];
  /** Chapter purpose in the owner decision journey. */
  conversionRole: FmReportChapterConversionRole;
  /** Complete served-data, action, expert, and non-ready-state contract. */
  recipe: FmReportChapterRecipeDescriptor;
  summary?: string;
  eyebrow?: string;
  tone?: FmReportChapterTone;
  /**
   * The chapter band's pace: `compact` for a dense analytical band,
   * `spacious` for the airy band that lets one statement breathe.
   */
  density?: FmSectionDensity;
  /** `display` sets the chapter title at the statement tier. */
  scale?: FmElementScale;
  /**
   * `beside` puts the chapter header on a narrow start rail beside its
   * sections (the editorial split); it stacks below the desktop tier.
   */
  headline?: FmSectionHeadline;
  /** Owner-readable label for the single DS-owned disclosure that groups detail sections. */
  detailLabel?: string;
  /** Open the single expert disclosure when a deep link targets retained detail. */
  detailOpen?: boolean;
  /** Optional mutually exclusive expert views owned and rendered by the chapter shell. */
  detailViews?: FmReportChapterDetailView[];
  sections: FmReportChapterSectionDescriptor[];
  testId?: string;
}

/**
 * The rail label for a chapter: its own words when the rail needs shorter ones,
 * otherwise the chapter title. Nav label and section heading derive from ONE
 * source, so they cannot drift apart.
 */
export function chapterNavLabel(chapter: FmReportChapterDescriptor): string {
  return chapter.navLabel ?? chapter.title;
}
